import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ChevronRight, FolderTree, Trash2 } from "lucide-react";

interface SubOrg {
  id: string;
  name: string;
  client_id: string;
  created_at: string;
  archived: boolean;
}

interface AdminSubOrgsTableProps {
  clientId: string;
  clientName: string;
  onSelectSubOrg: (subOrg: SubOrg) => void;
  onCreateClick: () => void;
}

const fmtDate = (ts?: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy");
};

export const AdminSubOrgsTable = ({ clientId, clientName, onSelectSubOrg, onCreateClick }: AdminSubOrgsTableProps) => {
  const [subOrgs, setSubOrgs] = useState<SubOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    subOrgId: string;
  }>({ open: false, title: "", description: "", subOrgId: "" });
  const { toast } = useToast();

  const handleDeleteSubOrg = async (reason: string) => {
    const subOrgId = confirmDialog.subOrgId;
    try {
      setDeleteLoading(true);
      const { error } = await (supabase as any).rpc("admin_delete_sub_org", {
        p_sub_org_id: subOrgId,
        p_reason: reason
      });
      if (error) throw error;
      toast({ title: "Success", description: "Sub-organization deleted successfully" });
      setConfirmDialog(prev => ({ ...prev, open: false }));
      loadSubOrgs();
    } catch (error: any) {
      console.error("Error deleting sub-org:", error);
      toast({ title: "Error", description: error.message || "Failed to delete sub-organization", variant: "destructive" });
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    loadSubOrgs();
  }, [clientId]);

  const loadSubOrgs = async () => {
    try {
      setLoading(true);
      console.log("[AdminSubOrgsTable] Calling admin_list_sub_orgs_all RPC");
      const { data, error } = await (supabase as any).rpc("admin_list_sub_orgs_all");

      if (error) {
        console.error("[AdminSubOrgsTable] RPC error:", error);
        throw error;
      }

      console.log("[AdminSubOrgsTable] RPC returned", data?.length, "sub-orgs");
      // Filter by clientId client-side
      const filtered = (data || []).filter((so: SubOrg) => so.client_id === clientId);
      // Sort by name ascending for display
      filtered.sort((a: SubOrg, b: SubOrg) => (a.name || "").localeCompare(b.name || ""));
      setSubOrgs(filtered);
    } catch (error) {
      console.error("Error loading sub-organizations:", error);
      toast({
        title: "Error",
        description: "Failed to load sub-organizations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredSubOrgs = subOrgs.filter(s =>
    !filter || s.name?.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center p-8">Loading sub-organizations...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <Input
          placeholder="Search sub-organizations..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        <Button onClick={onCreateClick}>
          Create Organization
        </Button>
      </div>

      {filteredSubOrgs.length === 0 ? (
        <div className="py-10 text-sm text-muted-foreground text-center">
          No sub-organizations found under {clientName}.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sub-Organization</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubOrgs.map((subOrg) => (
                <TableRow 
                  key={subOrg.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelectSubOrg(subOrg)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FolderTree className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{subOrg.name}</div>
                        <div className="text-xs text-muted-foreground">{subOrg.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{fmtDate(subOrg.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => onSelectSubOrg(subOrg)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmDialog({
                          open: true,
                          title: "Delete Sub-Organization",
                          description: `⚠️ This will permanently delete "${subOrg.name}" and ALL portfolio companies, assessments, findings, responses, and evidence underneath it. This action cannot be undone.`,
                          subOrgId: subOrg.id,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={handleDeleteSubOrg}
        confirmText="Delete Permanently"
        variant="destructive"
        loading={deleteLoading}
      />
    </div>
  );
};
